import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";
import "@/App.css";
import { I18nProvider } from "@/lib/i18n";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Parcelles from "@/pages/Parcelles";
import Pisciculture from "@/pages/Pisciculture";
import Fournisseurs from "@/pages/Fournisseurs";
import Clients from "@/pages/Clients";
import Paiements from "@/pages/Paiements";
import Layout from "@/components/layout/Layout";
import Timeline from "@/pages/Timeline";
import Utilisateurs from "@/pages/Utilisateurs";
import Travaux from "@/pages/Travaux";
import Commander from "@/pages/Commander";
import Sante from "@/pages/Sante";
import Equipements from "@/pages/Equipements";
import Equipes from "@/pages/Equipes";
import Decouvrir from "@/pages/Decouvrir";
import Assistant from "@/pages/Assistant";
import Analytics from "@/pages/Analytics";
import Transformation from "@/pages/Transformation";
import Parametres from "@/pages/Parametres";
import Licences from "@/pages/Licences";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/components/AuthCallback";
import AiChatWidget from "@/components/AiChatWidget";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "invité") return <Navigate to="/decouvrir" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function GuestOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">…</div>;
  if (user && user.role !== "invité") return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  // Retour Google OAuth : traiter le session_id AVANT toute route protégée
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <Routes>
      <Route path="/login" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/commander" element={<Commander />} />
      <Route path="/decouvrir" element={<Decouvrir />} />
      <Route path="/" element={<Protected><Dashboard /></Protected>} />
      <Route path="/parcelles" element={<Protected><Parcelles /></Protected>} />
      <Route path="/timeline" element={<Protected><Timeline /></Protected>} />
      <Route path="/travaux" element={<Protected><Travaux /></Protected>} />
      <Route path="/sante" element={<Protected><Sante /></Protected>} />
      <Route path="/equipements" element={<Protected roles={["admin", "gérant"]}><Equipements /></Protected>} />
      <Route path="/equipes" element={<Protected roles={["admin", "gérant"]}><Equipes /></Protected>} />
      <Route path="/pisciculture" element={<Protected><Pisciculture /></Protected>} />
      <Route path="/fournisseurs" element={<Protected><Fournisseurs /></Protected>} />
      <Route path="/clients" element={<Protected><Clients /></Protected>} />
      <Route path="/paiements" element={<Protected><Paiements /></Protected>} />
      <Route path="/utilisateurs" element={<Protected roles={["admin"]}><Utilisateurs /></Protected>} />
      <Route path="/assistant" element={<Protected><Assistant /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/transformation" element={<Protected><Transformation /></Protected>} />
      <Route path="/parametres" element={<Protected roles={["admin"]}><Parametres /></Protected>} />
      <Route path="/licences" element={<Protected roles={["admin"]}><Licences /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {location.pathname !== "/assistant" && <AiChatWidget />}
    </>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}
